import axios from 'axios';
import {CONFIG} from '../../appConfig';
import {types} from '../js/types';

/**
 * @private
 * @param {String|Number} status
 * @param {Object} body
 * @param {String} reqUrl
 * @param {String} message
 * @param {Boolean} cancelled
 * @param {String} code
 * @constructor
 */
function ReqError(status, body, reqUrl, message, cancelled, code) {
  if (types(status).isNumber) status = status.toString();
  this.name = 'ReqError';
  this.exception = message;
  this.message = cancelled ? message : CONFIG.ERROR_STATUS[status] || CONFIG.ERROR_DEFAULT;
  this.stack = (new Error()).stack;
  this.body = body;
  this.reqUrl = reqUrl;
  this.cancelled = cancelled;
  this.axiosErrorCode = code;
}
ReqError.prototype = Object.create(Error.prototype);

/**
 * @name request
 * @desc Wrapper to the axios library.
 * @param {Object} opts
 * @returns {Promise} Returns a promise-wrapped xhr request
 * @memberof utils
 * @example
 * let cancel = null;
 * const opts = {
 *  cancelFn: c => cancel = c, // *(Function)*: Optional. 'cancel' should be a value stored outside the opts
 *  timeoutConfig: {
 *    onRequestTimeoutCallback: *(Function)*: // used in lib/actions/request.js
 *  }
 *  headers: {}, // *(Object)*: Optional. Object of custom headers for the request
 *  origin: 'http://www.isobar.com/',
 *  endpoint: `fake/endpoint`,
 *  method: 'POST',
 *  data: {a:'b'}, // post data
 *  params: {c:'d'} // query string; ?c=d
 * };
 * // if you want to cancel an existing request
 * if (cancel !== null) {
 *  cancel();
 *  cancel = null;
 * }
 * const req = utils.request(opts)
 *  .then(
 *   resp => onSuccess(resp),
 *   // if request was cancelled; promise is rejected and err.cancelled === true
 *   err => onError(err)
 *  )
 *  // this is only necessary if you use cancelFn option
 *  .then(() => cancel = null);
 */
export default function (opts) {

  let url, withCredentials;
  const headers = Object.assign({'Content-Type': 'application/json'}, opts.headers || {}),
    method = opts.method || 'GET',
    params = opts.params || {},
    timeoutConfig = opts.timeoutConfig || {};

  if (opts.origin === CONFIG.SOLR_ORIGIN_URL) {
    if (CONFIG.SOLR_API_KEY) Object.assign(headers, {'x-api-key': CONFIG.SOLR_API_KEY});
    if (CONFIG.COUNTRY) Object.assign(params, {cor: CONFIG.COUNTRY});
    if (CONFIG.LOCALE) Object.assign(params, {locale: CONFIG.LOCALE});
    if (CONFIG.SOLR_DTO) Object.assign(params, {dto: CONFIG.SOLR_DTO});

    url = `${opts.origin}${CONFIG.BRAND.toLowerCase()}/${CONFIG.CHANNEL.toLowerCase()}/${opts.endpoint}`;
  } else {
    url = opts.origin + opts.endpoint;
  }

  if (opts.origin === CONFIG.GMA_ORIGIN_URL) {
    Object.assign(headers, {
      'locale': CONFIG.LOCALE,
      'domain_country_of_residence': CONFIG.COUNTRY,
      'BRAND': CONFIG.BRAND,
      'CHANNEL': CONFIG.CHANNEL
    // add custom gma headers provided via initGmi
    }, (types(CONFIG.GMA_HEADERS).isObject) ? CONFIG.GMA_HEADERS : {});
    withCredentials = true;
  } else if (opts.origin === CONFIG.CDA_ORIGIN_URL) {
    Object.assign(headers, {
    // add custom cda headers provided via initGmi
    }, (types(CONFIG.CDA_HEADERS).isObject) ? CONFIG.CDA_HEADERS : {});
    withCredentials = true;
  } else {
    withCredentials = opts.withCredentials || false;
  }

  const config = {
    headers,
    method,
    params,
    url,
    withCredentials,
    ...(timeoutConfig?.onRequestTimeoutCallback && { timeout: CONFIG.SERVICE_REQUEST_TIMEOUT })
  };
  if (['POST', 'PUT'].includes(method.toUpperCase())) config.data = opts.data || {};
  if (opts.cancelFn) config.cancelToken = new axios.CancelToken(opts.cancelFn);

  return axios.request(config)
    .then(resp => {
      return opts.fullResponse ? resp : resp.data;
    })
    .catch(err => {
      const {message, code} = err;
      let body = {}, status = 0, cancelled = false;
      if (err instanceof axios.Cancel) {
        cancelled = true;
      } else if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        body = err.response.data;
        status = err.response.status;
      } else if (err.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        body = err.request;
        status = err.status;
      } else if (err.status) {
        // Something happened in setting up the request that triggered an Error
        status = err.status;
      }
      // Else, something happened in setting up the request that triggered an Error
      throw new ReqError(status, body, url, message, cancelled, code);
    });
}
